<script setup>
import { ref } from 'vue'
import { SUPPLIER_PATHS } from '@supplier/router/routeNames'
import { useSupplierSessionStore } from '@supplier/stores/useSupplierSessionStore'
import { useI18n } from 'vue-i18n'

import { useSupplierNav } from '@supplier/layouts/composables/useSupplierNav'

import { SUPPORT_URL } from '@supplier/consts'

import AppButton from '@ui/app-button/AppButton.vue'
import AppIconButton from '@ui/app-icon-button/AppIconButton.vue'
import AppSelect from '@ui/app-select/AppSelect.vue'

defineProps({
  compact: {
    type: Boolean,
    default: false,
  },
})

const { t } = useI18n()
const session = useSupplierSessionStore()
const { items: navItems } = useSupplierNav()

const isLoggingOut = ref(false)

async function logout() {
  isLoggingOut.value = true
  await session.logout()
}
</script>

<template>
  <app-select
    mode="popover"
    placement="bottom-end"
    :dropdown-width="240"
    :placeholder="session.displayName"
    class="supplier-user-menu"
    data-element="supplier-header-user-menu"
  >
    <template #trigger>
      <app-icon-button
        v-if="compact"
        icon="Menu"
        variant="no-fill"
        :aria-label="t('ui.supplier_portal.header.open_menu')"
        data-element="supplier-header-menu-btn"
      />
      <app-button
        v-else
        variant="text"
        size="sm"
        right-icon="ChevronDown"
        data-element="supplier-header-user-menu-btn"
      >
        {{ session.displayName }}
      </app-button>
    </template>

    <template #items="{ close }">
      <div class="supplier-user-menu-items">
        <template v-if="compact">
          <app-button
            v-for="item in navItems"
            :key="item.key"
            :variant="item.isActive ? 'secondary' : 'menu'"
            block
            :to="item.to"
            :aria-current="item.isActive ? 'page' : undefined"
            :data-element="`supplier-header-menu-${item.key}-link`"
            @click="close()"
          >
            {{ item.label }}
          </app-button>
          <div class="supplier-user-menu-divider" />
        </template>
        <app-button
          variant="menu"
          block
          spaced="right"
          :to="SUPPLIER_PATHS.PROFILE"
          data-element="supplier-header-menu-profile-link"
          @click="close()"
        >
          {{ t('ui.supplier_portal.titles.profile') }}
        </app-button>
        <app-button
          variant="menu"
          block
          spaced="right"
          :to="SUPPORT_URL"
          data-element="supplier-header-menu-support-link"
          @click="close()"
        >
          {{ t('ui.supplier_portal.header.menu_support') }}
        </app-button>
        <app-button
          variant="menu"
          block
          spaced="right"
          :loading="isLoggingOut"
          data-element="supplier-header-menu-logout-btn"
          @click="logout"
        >
          {{ t('ui.supplier_portal.header.menu_logout') }}
        </app-button>
      </div>
    </template>
  </app-select>
</template>

<style lang="scss" scoped>
.supplier-user-menu {
  &-items {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  &-divider {
    margin: 8px 0;
    height: 1px;
    background-color: var(--g-neutral-6);
  }
}
</style>
